import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import '../backlog/backlog.css';
import './samplingHistory.css';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { CheckedCombo } from '../../components/CheckedCombo';
import { AppDialog } from '../../components/AppDialog';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading';
import { triggerGridExport } from '../../components/AppToolbar/ExportMenu';
import type { HistLedgerRow, HistMatrixRow, CellState } from './samplingHistoryData';
import { deriveSamplingHistory, type SamplingHistoryApiRow } from './samplingHistoryDerive';

// ─────────────────────────────────────────────────────────────────────────────
// Sample Scheduling → Sampling History  (read-only)
//
// Ported from the approved prototype (Sampling-History-v24.html): a month-by-
// month record of every scheduled sampling and whether it was collected, so a
// site's history and any slipped obligation is visible at a glance. The query
// filters drive the calendar, the log and the KPIs together.
//
// Rendered with THIS product's shared components (FormTitle / AppToolbar /
// AppDataGrid / CheckedCombo / AppDialog / useNotify) — the compliance calendar
// is an inherent PIVOT (site x month), so it uses a scoped table rather than the
// flat AppDataGrid (§18.6). Data comes from GET /api/v1/sampling-history
// (dbo.vw_RecurrenceJobSampling, job level); the month columns, calendar pivot,
// point×month drill and per-COC frequency are DERIVED client-side
// (samplingHistoryDerive.ts). The filter layout, header hint, per-tab Excel/PDF
// export and three-chart panel mirror the prototype.
// ─────────────────────────────────────────────────────────────────────────────

type TabKey = 'cal' | 'ledger' | 'charts';

const FREQ_CLASS = (fk: string) => (['m', 'w', 'bw', 'sm', 'q', 'o'].includes(fk) ? fk : 'o');
const FREQ_LABELS: Record<string, string> = {
  m: 'Monthly', w: 'Weekly', bw: 'Biweekly', sm: 'Semi-monthly', q: 'Quarterly', o: 'Other',
};

/** Sampled → green · Partially sampled → amber · Not sampled → red. */
function statusPill(s: string): 'green' | 'amber' | 'red' {
  const t = s.toLowerCase();
  if (t.startsWith('not')) return 'red';
  if (t.startsWith('partial')) return 'amber';
  return 'green';
}

// Calendar glyphs match the prototype: ✓ Sampled · ! Partially sampled · ✗ Not
// sampled · · No job.
const CELL_MARK: Record<CellState, string> = { ok: '✓', part: '!', miss: '✗', na: '·' };
const CELL_LABEL: Record<CellState, string> = { ok: 'Sampled', part: 'Partially sampled', miss: 'Not sampled', na: 'No job' };

/** Scheduled MM/DD/YYYY → the calendar period key ('2026-7'), matching the
 *  derived month keys (`YYYY-M`, no leading zero). */
const periodOf = (scheduled: string): string => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(scheduled);
  return m ? `${m[3]}-${Number(m[1])}` : '';
};

// ── Sort helpers for the Sampling Log grid (so date/ratio columns sort by value,
// not by their display string). ──
/** "MM/DD/YYYY" → epoch ms, or NaN when blank/unparseable. */
const dateMs = (s: string): number => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s || '');
  return m ? Date.UTC(+m[3]!, +m[1]! - 1, +m[2]!) : NaN;
};
/** "sampled/total" → the ratio 0..1, or NaN when unparseable. */
const fracVal = (s: string): number => {
  const m = /^(\d+)\s*\/\s*(\d+)/.exec(s || '');
  if (!m) return NaN;
  const t = +m[2]!;
  return t ? +m[1]! / t : 0;
};
/** Numeric compare that sorts blanks (NaN) to the bottom (asc). Returns -1/0/1. */
const cmpNum = (x: number, y: number): number => {
  const ax = Number.isNaN(x) ? -Infinity : x;
  const ay = Number.isNaN(y) ? -Infinity : y;
  return ax === ay ? 0 : ax < ay ? -1 : 1;
};

/** 4-digit year from an ISO ("2026-04-01") or US ("04/01/2026") date string. */
const yearOf = (s: string | null): string => {
  const m = /^(\d{4})-/.exec(s ?? '') ?? /\/(\d{4})/.exec(s ?? '');
  return m ? m[1]! : '';
};

export default function SamplingHistoryPage() {
  const notify = useNotify();

  const [clientSel, setClientSel] = useState<string[]>([]);
  const [projectSel, setProjectSel] = useState<string[]>([]);
  const [siteSel, setSiteSel] = useState<string[]>([]);
  const [pointSel, setPointSel] = useState<string[]>([]);
  const [periodSel, setPeriodSel] = useState<string[]>([]);
  const [freqSel, setFreqSel] = useState<string[]>([]);
  const [statusSel, setStatusSel] = useState<string[]>([]);
  const [year, setYear] = useState('2026');
  const [tab, setTab] = useState<TabKey>('cal');
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<string | null>(null);
  // Sort direction for the calendar Site column + the drill Sample column.
  const [calDir, setCalDir] = useState<'asc' | 'desc'>('asc');
  const [drillDir, setDrillDir] = useState<'asc' | 'desc'>('asc');
  const [visibleLedger, setVisibleLedger] = useState<HistLedgerRow[]>([]);
  // Maximize the COMPLIANCE CALENDAR grid to the content region (ALPACA /
  // AppDataGrid parity — same icon + Esc-to-restore). Bespoke here because the
  // calendar is a hand-rolled pivot table, not the shared <AppDataGrid> (the
  // Sampling Log tab, which DOES use <AppDataGrid>, gets its own maximize).
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaximized(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized]);

  // ── Load the job-level rows from the DB view; derive the whole screen ──
  const [apiRows, setApiRows] = useState<SamplingHistoryApiRow[]>([]);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/sampling-history');
      const data = (res.data?.data ?? []) as SamplingHistoryApiRow[];
      setApiRows(data);
      setLoadMs(Math.round(performance.now() - t0));
    } catch {
      setError('Could not load sampling history.');
      setApiRows([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // Distinct years present in the data (newest first) — drives the Year dropdown.
  const years = useMemo(() => {
    const s = new Set<string>();
    for (const a of apiRows) { const y = yearOf(a.scheduled); if (y) s.add(y); }
    return [...s].sort((a, b) => b.localeCompare(a));
  }, [apiRows]);
  // Keep the selected year valid — default to the newest available year.
  useEffect(() => {
    if (years.length && !years.includes(year)) setYear(years[0]!);
  }, [years, year]);

  // Year is the TOP-LEVEL scope: the calendar months, log, charts, KPIs and every
  // filter option are all computed from the selected year's jobs.
  const apiRowsForYear = useMemo(
    () => apiRows.filter((a) => yearOf(a.scheduled) === year),
    [apiRows, year],
  );
  const derived = useMemo(() => deriveSamplingHistory(apiRowsForYear), [apiRowsForYear]);
  const { months, ledger, matrix } = derived;

  // Chart colours come from the theme tokens (green/amber/red/orange) — read the
  // computed values once, since an SVG `fill`/`stroke` attribute can't resolve
  // var(). Keeps the charts in step with the status-pill palette.
  const chartColors = useMemo(() => {
    const read = (name: string, fb: string) =>
      (typeof window !== 'undefined'
        ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
        : '') || fb;
    return {
      ok: read('--ai-green', '#0f8a55'),
      part: read('--ai-amber', '#b45309'),
      miss: read('--ai-red', '#d24a3a'),
      line: read('--ai-orange', '#e8670a'),
      grid: read('--ai-line', '#e3e6ec'),
    };
  }, []);

  const periodKeyByLabel = useMemo(
    () => new Map(months.map((m) => [m.label, m.mk] as const)),
    [months],
  );

  // Cascaded filter options (Excel-style): each dropdown lists ONLY the values
  // still available given the OTHER active filters, so its trigger count
  // ("All client (2)") reflects what's actually pickable and narrows as you filter.
  const passesExcept = useCallback((r: HistLedgerRow, except: string): boolean => {
    if (except !== 'client' && clientSel.length && !clientSel.includes(r.client)) return false;
    if (except !== 'project' && projectSel.length && !projectSel.includes(r.project)) return false;
    if (except !== 'site' && siteSel.length && !siteSel.includes(r.site)) return false;
    if (except !== 'status' && statusSel.length && !statusSel.includes(r.status)) return false;
    if (except !== 'freq' && freqSel.length && !freqSel.includes(r.fk)) return false;
    if (except !== 'period' && periodSel.length && !periodSel.includes(periodOf(r.scheduled))) return false;
    if (except !== 'point' && pointSel.length) {
      const pts = r.points.split(',').map((p) => p.trim());
      if (!pts.some((p) => pointSel.includes(p))) return false;
    }
    return true;
  }, [clientSel, projectSel, siteSel, statusSel, freqSel, periodSel, pointSel]);

  const optsExcept = useCallback((except: string, val: (r: HistLedgerRow) => string[]): string[] => {
    const seen = new Set<string>();
    for (const r of ledger) if (passesExcept(r, except)) for (const v of val(r)) if (v) seen.add(v);
    return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [ledger, passesExcept]);

  const clientOpts = useMemo(() => optsExcept('client', (r) => [r.client]), [optsExcept]);
  const projectOpts = useMemo(() => optsExcept('project', (r) => [r.project]), [optsExcept]);
  const siteOpts = useMemo(() => optsExcept('site', (r) => [r.site]), [optsExcept]);
  const pointOpts = useMemo(() => optsExcept('point', (r) => r.points.split(',').map((p) => p.trim())), [optsExcept]);
  const statusOpts = useMemo(() => optsExcept('status', (r) => [r.status]), [optsExcept]);
  const freqKeys = useMemo(() => optsExcept('freq', (r) => [r.fk]), [optsExcept]);
  const freqOptLabels = useMemo(() => freqKeys.map((k) => FREQ_LABELS[k] ?? k), [freqKeys]);
  // Period options in chronological month order (mk keys → labels).
  const periodLabels = useMemo(() => {
    const keys = new Set(optsExcept('period', (r) => [periodOf(r.scheduled)]));
    return months.filter((m) => keys.has(m.mk)).map((m) => m.label);
  }, [optsExcept, months]);

  // Site name ↔ COC maps (the matrix keys on site name, the ledger on COC).
  const cocOfSite = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of matrix) m.set(r.site, r.coc);
    return m;
  }, [matrix]);
  const siteKeyByCoc = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of matrix) m.set(r.coc, r.site);
    return m;
  }, [matrix]);
  const openDrill = useCallback((site: string) => setDrill(site), []);

  // Boolean (not the raw number) so `{anyFilter && …}` can never render a stray
  // "0" when the last operand (statusSel.length) is 0.
  const anyFilter = !!(
    clientSel.length || projectSel.length || siteSel.length || pointSel.length ||
    periodSel.length || freqSel.length || statusSel.length);

  const filteredLedger = useMemo(() => {
    const siteCocs = new Set(siteSel.map((s) => cocOfSite.get(s)).filter(Boolean) as string[]);
    const pointSet = new Set(pointSel);
    const periodSet = new Set(periodSel);
    return ledger.filter((r) => {
      if (clientSel.length && !clientSel.includes(r.client)) return false;
      if (projectSel.length && !projectSel.includes(r.project)) return false;
      if (siteSel.length && !siteCocs.has(r.coc)) return false;
      if (statusSel.length && !statusSel.includes(r.status)) return false;
      if (freqSel.length && !freqSel.includes(r.fk)) return false;
      if (periodSet.size && !periodSet.has(periodOf(r.scheduled))) return false;
      if (pointSet.size) {
        const pts = r.points.split(',').map((p) => p.trim());
        if (!pts.some((p) => pointSet.has(p))) return false;
      }
      return true;
    });
  }, [ledger, clientSel, projectSel, siteSel, pointSel, periodSel, freqSel, statusSel, cocOfSite]);

  // All three tabs reflect the SAME filtered job set. The ledger / charts / KPIs
  // use filteredLedger; the calendar cells AND the drill RE-DERIVE from the filtered
  // jobs (mapped back to their API rows by jobId), so filters like Period / Status /
  // Site point genuinely reshape the calendar and drill — not just row visibility.
  // The month COLUMNS stay the full set (see `months`) so the grid shape is stable.
  const filteredJobIds = useMemo(() => new Set(filteredLedger.map((r) => r.jobId)), [filteredLedger]);
  const filteredDerived = useMemo(
    () => deriveSamplingHistory(apiRowsForYear.filter((a) => a.jobId != null && filteredJobIds.has(a.jobId))),
    [apiRowsForYear, filteredJobIds],
  );
  const filteredMatrix = filteredDerived.matrix;

  // "Gap" sites (any month partial/not-sampled) for the drill Prev/Next nav —
  // follows the filtered calendar.
  const gapList = useMemo(
    () => filteredMatrix.filter((r) => Object.values(r.cells).some((v) => v === 'part' || v === 'miss')).map((r) => r.site),
    [filteredMatrix],
  );
  const dmStep = useCallback((dir: number) => {
    setDrill((cur) => {
      if (!gapList.length) return cur;
      const i = cur ? gapList.indexOf(cur) : -1;
      const next = (i < 0 ? 0 : i + dir + gapList.length) % gapList.length;
      return gapList[next]!;
    });
  }, [gapList]);

  // Compliance calendar is GROUPED BY CLIENT (prototype grouprow splitter): each
  // client heads a row spanning the grid, then its sites. Sites sort by name in
  // the direction the Site header toggles (calDir); clients stay A→Z.
  const calRows = useMemo(() => {
    const dir = calDir === 'desc' ? -1 : 1;
    const byClient = new Map<string, HistMatrixRow[]>();
    for (const r of filteredMatrix) {
      const k = r.client || '—';
      (byClient.get(k) ?? byClient.set(k, []).get(k)!).push(r);
    }
    const clients = [...byClient.keys()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const out: Array<{ group: string } | { row: HistMatrixRow }> = [];
    for (const c of clients) {
      out.push({ group: c });
      const rows = byClient.get(c)!.slice().sort((a, b) =>
        dir * a.site.localeCompare(b.site, undefined, { numeric: true }));
      for (const r of rows) out.push({ row: r });
    }
    return out;
  }, [filteredMatrix, calDir]);

  // KPIs recompute from the filtered ledger (prototype parity: 68% / 330 / 226 / 74·30 / 40).
  const kpi = useMemo(() => {
    const jobs = filteredLedger.length;
    const sampled = filteredLedger.filter((r) => r.status === 'Sampled').length;
    const partial = filteredLedger.filter((r) => r.status === 'Partially sampled').length;
    const notSampled = filteredLedger.filter((r) => r.status === 'Not sampled').length;
    const rate = jobs ? Math.round((sampled / jobs) * 100) : 0;
    const gap = new Set(filteredLedger.filter((r) => r.status !== 'Sampled').map((r) => r.site)).size;
    return { jobs, sampled, partial, notSampled, rate, gap };
  }, [filteredLedger]);

  // Per-month buckets (bar + line share these).
  const monthChart = useMemo(() =>
    months.map((mo) => {
      const inMonth = filteredLedger.filter((r) => periodOf(r.scheduled) === mo.mk);
      const sampled = inMonth.filter((r) => r.status === 'Sampled').length;
      const partial = inMonth.filter((r) => r.status === 'Partially sampled').length;
      const not = inMonth.filter((r) => r.status === 'Not sampled').length;
      const total = inMonth.length;
      return {
        month: mo.label.replace(/ '\d{2}$/, ''),
        Sampled: sampled, Partial: partial, 'Not sampled': not,
        rate: total ? Math.round((sampled / total) * 100) : 0,
      };
    }), [months, filteredLedger]);

  const statusChart = useMemo(() => ([
    { name: 'Sampled', value: kpi.sampled, color: chartColors.ok },
    { name: 'Partially sampled', value: kpi.partial, color: chartColors.part },
    { name: 'Not sampled', value: kpi.notSampled, color: chartColors.miss },
  ].filter((d) => d.value > 0)), [kpi, chartColors]);

  const clearAll = () => {
    setClientSel([]); setProjectSel([]); setSiteSel([]); setPointSel([]);
    setPeriodSel([]); setFreqSel([]); setStatusSel([]);
  };

  const refresh = () => {
    void load().then(() => { if (!error) notify.success('Refreshed.'); });
  };

  // ── Sampling Log ledger columns (auto-sized like the prototype; points clipped) ──
  const ledgerCols: AppColumn<HistLedgerRow>[] = useMemo(() => [
    { field: 'coc', header: 'COC' },
    {
      field: 'site', header: 'Site',
      render: (r) => (
        <span className="sh-sitelink" title="Open this site's point history"
          onClick={() => openDrill(siteKeyByCoc.get(r.coc) ?? r.site)}>{r.site}</span>
      ),
      text: (r) => r.site,
    },
    { field: 'client', header: 'Client' },
    { field: 'project', header: 'Project' },
    {
      field: 'fk', header: 'Frequency',
      render: (r) => <span className={`sh-fp ${FREQ_CLASS(r.fk)}`}>{r.frequency}</span>,
      text: (r) => r.frequency,
    },
    { field: 'jobId', header: 'Job ID' },
    // Sample sites (points) — clipped to ~200px with an ellipsis + hover title,
    // exactly like the prototype's .idlist column (plain text so AppDataGrid adds
    // the title tooltip automatically).
    { field: 'points', header: 'Sample sites (points)', width: 200 },
    { field: 'scheduled', header: 'Scheduled', align: 'center', compare: (a, b) => cmpNum(dateMs(a.scheduled), dateMs(b.scheduled)) },
    {
      field: 'collected', header: 'Collected', align: 'center',
      render: (r) => (
        <span>
          {r.collected}
          {r.late && <span className="sh-latetag" title="Collected after the scheduled date">{r.late}</span>}
        </span>
      ),
      text: (r) => `${r.collected} ${r.late}`.trim(),
      // Sort by the collected DATE (the display text carries the "+Nd" tag, which
      // would break a string/date sort); blanks (never collected) sort last.
      compare: (a, b) => cmpNum(dateMs(a.collected), dateMs(b.collected)),
    },
    { field: 'sampledTotal', header: 'Sampled/Total', align: 'center', compare: (a, b) => cmpNum(fracVal(a.sampledTotal), fracVal(b.sampledTotal)) },
    {
      field: 'status', header: 'Status', align: 'center',
      render: (r) => <span className={`sh-pill ${statusPill(r.status)}`}>{r.status}</span>,
      text: (r) => r.status,
    },
  ], [openDrill, siteKeyByCoc]);

  // ── Per-tab export payload (prototype "Export current tab") ──
  const buildExport = (): { filename: string; headers: string[]; rows: string[][] } => {
    if (tab === 'cal') {
      return {
        filename: 'Sampling-History-Calendar',
        headers: ['Site', 'COC', ...months.map((m) => m.label)],
        rows: filteredMatrix.map((r) => [
          r.site, r.coc,
          ...months.map((m) => CELL_LABEL[(r.cells[m.mk] ?? 'na') as CellState]),
        ]),
      };
    }
    if (tab === 'charts') {
      return {
        filename: 'Sampling-History-Monthly',
        headers: ['Month', 'Sampled', 'Partial', 'Not sampled', 'Fully-sampled %'],
        rows: monthChart.map((m) => [
          m.month, String(m.Sampled), String(m.Partial), String(m['Not sampled']), `${m.rate}%`,
        ]),
      };
    }
    const m = gridExportMatrix(visibleLedger, ledgerCols);
    return { filename: 'Sampling-History-Log', headers: m.headers, rows: m.rows };
  };

  const exportTab = (kind: 'xlsx' | 'pdf') => {
    const { filename, headers, rows } = buildExport();
    void runLoading((async () => {
      try {
        if (kind === 'xlsx') {
          await triggerGridExport('xlsx', filename, headers, rows);
        } else {
          const { jsPDF } = await import('jspdf');
          await import('jspdf-autotable');
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          (doc as unknown as { autoTable: (o: unknown) => void }).autoTable({
            head: [headers], body: rows, styles: { fontSize: 9, cellPadding: 4 },
            headStyles: { fillColor: [224, 123, 57] }, margin: { top: 40, left: 20, right: 20 },
            didDrawPage: () => doc.text(filename, 20, 26),
          });
          doc.save(`${filename}.pdf`);
        }
        notify.success(`Exported to ${kind === 'xlsx' ? 'Excel' : 'PDF'} successfully.`);
      } catch {
        notify.error('Export failed.');
      }
    })(), { blocking: true, label: 'Exporting…' });
  };


  return (
    <div className="bl-page sh-scope">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sample Scheduling</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Sampling History</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="samplinghistory" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <AppToolbar onRefresh={refresh} refreshing={loading} />

        {error && (
          <div className="bl-err">
            <i className="ti ti-alert-circle" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 5 }} />
            {error}
          </div>
        )}

        {/* ── Filters — 4 vertical pairs (prototype layout) ── */}
        <div className="sh-filters">
          <div className="sh-fpair">
            <div className="sh-fg">
              <label>Client</label>
              <CheckedCombo options={clientOpts} values={clientSel} onChange={setClientSel}
                placeholder="All client" unit="clients" ariaLabel="Filter by client" showCount />
            </div>
            <div className="sh-fg">
              <label>Project</label>
              <CheckedCombo options={projectOpts} values={projectSel} onChange={setProjectSel}
                placeholder="All project" unit="projects" ariaLabel="Filter by project" showCount pageSize={12} />
            </div>
          </div>
          <div className="sh-fpair">
            <div className="sh-fg">
              <label>Site</label>
              <CheckedCombo options={siteOpts} values={siteSel} onChange={setSiteSel}
                placeholder="All site" unit="sites" ariaLabel="Filter by site" showCount pageSize={12} />
            </div>
            <div className="sh-fg">
              <label>Site point</label>
              <CheckedCombo options={pointOpts} values={pointSel} onChange={setPointSel}
                placeholder="All site point" unit="points" ariaLabel="Filter by site point" showCount pageSize={12} />
            </div>
          </div>
          <div className="sh-fpair">
            <div className="sh-fg">
              <label>Year</label>
              <select className="sh-yr" value={year} onChange={(e) => setYear(e.target.value)}
                aria-label="Year" disabled={years.length <= 1}>
                {years.length === 0 && <option value={year}>{year}</option>}
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="sh-fg">
              <label>Period</label>
              <CheckedCombo
                options={periodLabels}
                values={periodSel.map((mk) => months.find((m) => m.mk === mk)?.label ?? mk)}
                onChange={(labels) => setPeriodSel(labels.map((l) => periodKeyByLabel.get(l) ?? l))}
                placeholder="All period" unit="periods" ariaLabel="Filter by period" showCount />
            </div>
          </div>
          <div className="sh-fpair">
            <div className="sh-fg">
              <label>Frequency</label>
              <CheckedCombo
                options={freqOptLabels}
                values={freqSel.map((k) => FREQ_LABELS[k] ?? k)}
                onChange={(labels) => setFreqSel(freqKeys.filter((k) => labels.includes(FREQ_LABELS[k] ?? k)))}
                placeholder="All frequency" unit="frequencies" ariaLabel="Filter by frequency" showCount />
            </div>
            <div className="sh-fg">
              <label>Status</label>
              <CheckedCombo options={statusOpts} values={statusSel} onChange={setStatusSel}
                placeholder="All status" unit="statuses" ariaLabel="Filter by status" showCount />
            </div>
          </div>
          <div className="sh-fg sh-actions">
            <label>&nbsp;</label>
            <button className="ca-btn" onClick={clearAll} disabled={!anyFilter} title="Clear filters"><i className="ti ti-filter-off" /> Clear filters</button>
          </div>
        </div>

        {/* View-state line (prototype .viewstate). */}
        <p className="sh-viewstate">
          {anyFilter ? 'Filtered' : 'All'} · <b>{kpi.jobs}</b> jobs
          {anyFilter && <span className="rst" onClick={clearAll}>↩ Clear filters</span>}
        </p>

        {/* ── KPIs ── */}
        <div className="sh-kpis">
          <div className="sh-kpi good"><div className="n">{kpi.rate}%</div><div className="l">Fully-sampled rate</div></div>
          <div className="sh-kpi"><div className="n">{kpi.jobs}</div><div className="l">{months.length
            ? `Jobs (${months[0]!.label.split(' ')[0]}${months.length > 1 ? '–' + months[months.length - 1]!.label.split(' ')[0] : ''})`
            : 'Jobs'}</div></div>
          <div className="sh-kpi good"><div className="n">{kpi.sampled}</div><div className="l">Sampled</div></div>
          <div className="sh-kpi alert"><div className="n">{kpi.partial}/{kpi.notSampled}</div><div className="l">Partial / Not sampled</div></div>
          <div className="sh-kpi alert"><div className="n">{kpi.gap}</div><div className="l">Sites with a gap</div></div>
        </div>

        {/* ── Tabs + per-tab export on ONE row (space-efficient) ── */}
        <div className="sh-tabrow">
          <div className="sh-tabs">
            <div className={`sh-tab${tab === 'cal' ? ' active' : ''}`} onClick={() => setTab('cal')}>Compliance calendar</div>
            <div className={`sh-tab${tab === 'ledger' ? ' active' : ''}`} onClick={() => setTab('ledger')}>Sampling Log</div>
            <div className={`sh-tab${tab === 'charts' ? ' active' : ''}`} onClick={() => setTab('charts')}>Charts</div>
          </div>
          <div className="sh-exportbar">
            <span className="ebl">Export current tab</span>
            <button className="sh-eb" onClick={() => exportTab('xlsx')} disabled={loading}>▦ Excel</button>
            <button className="sh-eb" onClick={() => exportTab('pdf')} disabled={loading}>🗎 PDF</button>
          </div>
        </div>

        {/* ── Tab: Compliance calendar (pivot) ── */}
        {tab === 'cal' && (
          <>
            <div className={`sh-matrix-card${maximized ? ' sh-maximized' : ''}`}>
              {/* Grid header bar — Maximize/Restore on the calendar grid itself
                  (the Sampling Log tab gets its own from <AppDataGrid>). */}
              <div className="sh-gridhead">
                <span className="sh-gridhead-sp" />
                <button type="button" className="sh-maxbtn" onClick={() => setMaximized((m) => !m)}
                  title={maximized ? 'Restore (Esc)' : 'Maximize'} aria-label={maximized ? 'Restore' : 'Maximize'}>
                  <i className={`ti ${maximized ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} aria-hidden="true" />
                </button>
              </div>
              <table className="sh-matrix">
                <thead>
                  <tr>
                    <th className="lead-h site-h sh-th-sort"
                      onClick={() => setCalDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                      title="Sort sites">
                      Site <span className="sh-sort">{calDir === 'asc' ? '▲' : '▼'}</span>
                    </th>
                    <th className="lead-h">COC</th>
                    {months.map((m) => {
                      const [mon, yr] = m.label.split(' ');
                      return <th key={m.mk}>{mon}<br />{yr}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {calRows.map((it, i) => ('group' in it ? (
                    <tr key={`g-${i}`} className="sh-grouprow">
                      <td colSpan={months.length + 2}>{it.group}</td>
                    </tr>
                  ) : (
                    <tr key={it.row.coc + it.row.site}>
                      <td className="site-c" onClick={() => openDrill(it.row.site)} title="Open this site's point history">{it.row.site}</td>
                      <td className="coc-c">{it.row.coc}</td>
                      {months.map((m) => {
                        const st = (it.row.cells[m.mk] ?? 'na') as CellState;
                        return (
                          <td key={m.mk} className="cell">
                            <span className={`sh-cx ${st}`} title={CELL_LABEL[st]}>{CELL_MARK[st]}</span>
                          </td>
                        );
                      })}
                    </tr>
                  )))}
                  {filteredMatrix.length === 0 && (
                    <tr><td className="coc-c" colSpan={months.length + 2} style={{ textAlign: 'center', padding: 20 }}>No sites match the current filters</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="sh-legend">
              <span className="li"><span className="sh-cx ok">✓</span> Sampled</span>
              <span className="li"><span className="sh-cx part">!</span> Partially sampled</span>
              <span className="li"><span className="sh-cx miss">✗</span> Not sampled</span>
              <span className="li"><span className="sh-cx na">·</span> No job</span>
            </div>
            <p className="sh-leadp">Real compliance calendar — green where sampled, amber/red where not. The query panel filters which sites appear.</p>
          </>
        )}

        {/* ── Tab: Sampling Log (ledger) ── */}
        {tab === 'ledger' && (
          <>
            <div className="bl-card bl-card-flex">
              <AppDataGrid<HistLedgerRow>
                rows={filteredLedger}
                columns={ledgerCols}
                getRowId={(r) => r.jobId}
                loading={loading}
                onVisibleRowsChange={setVisibleLedger}
                emptyText="No jobs match the current filters"
              />
            </div>
            <p className="sh-leadp">One row per job. <b>Collected</b> blank = never sampled; a <span className="sh-latetag">+Nd</span> tag flags a late collection.</p>
          </>
        )}

        {/* ── Tab: Charts (wide bar · rate line · outcome donut) ── */}
        {tab === 'charts' && (
          <>
            <div className="sh-charts">
              <div className="sh-chartcard sh-chart-wide">
                <h3>Monthly outcomes (jobs by status) — bar</h3>
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={monthChart} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="Sampled" stackId="a" fill={chartColors.ok} />
                    <Bar dataKey="Partial" stackId="a" fill={chartColors.part} />
                    <Bar dataKey="Not sampled" stackId="a" fill={chartColors.miss} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="sh-chart-legend">
                  <span className="li"><span className="sh-swatch" style={{ background: chartColors.ok }} /> Sampled</span>
                  <span className="li"><span className="sh-swatch" style={{ background: chartColors.part }} /> Partially sampled</span>
                  <span className="li"><span className="sh-swatch" style={{ background: chartColors.miss }} /> Not sampled</span>
                </div>
              </div>
              <div className="sh-chartcard">
                <h3>Fully-sampled % trend — line</h3>
                <ResponsiveContainer width="100%" height={230}>
                  <LineChart data={monthChart} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
                    <Tooltip formatter={(v: number) => `${v}%`} />
                    <Line type="monotone" dataKey="rate" name="Fully-sampled %" stroke={chartColors.line} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="sh-chartcard">
                <h3>Overall status — pie</h3>
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie data={statusChart} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {statusChart.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="sh-chart-legend">
                  <span className="li"><span className="sh-swatch" style={{ background: chartColors.ok }} /> Sampled ({kpi.sampled})</span>
                  <span className="li"><span className="sh-swatch" style={{ background: chartColors.part }} /> Partial ({kpi.partial})</span>
                  <span className="li"><span className="sh-swatch" style={{ background: chartColors.miss }} /> Not ({kpi.notSampled})</span>
                </div>
              </div>
            </div>
            <p className="sh-leadp">Charts recompute with the filters above — pick a client, project, site or month and the trend follows.</p>
          </>
        )}
      </div>

      {/* ── Site drill-down popup — point × month grid (prototype dm-dgrid) ── */}
      <AppDialog
        open={!!drill}
        title={drill ?? ''}
        icon="ti-calendar-check"
        width={880}
        className="sh-scope"
        onClose={() => setDrill(null)}
        footer={drill ? (
          <div className="sh-dm-foot">
            <span className="sh-dm-note">
              Point-level status is the real per-sample split — each point, per month:
              green when the sample was collected, red when it was not.
            </span>
            <span className="sh-dm-nav">
              <button className="ca-btn" onClick={() => dmStep(-1)}>‹ Prev gap site</button>
              <button className="ca-btn ca-btn-solid" onClick={() => dmStep(1)}>Next gap site ›</button>
            </span>
          </div>
        ) : undefined}
      >
        {drill && (() => {
          const g = filteredDerived.sitegrid[drill];
          const siteMeta = filteredDerived.meta[drill];
          const points = [...(g?.points ?? [])].sort((a, b) =>
            (drillDir === 'desc' ? -1 : 1) * a.localeCompare(b, undefined, { numeric: true }));
          return (
            <>
              <div className="sh-dm-sub">
                COC {siteMeta?.coc ?? '—'} · {siteMeta?.client ?? ''}{siteMeta?.project ? ` · ${siteMeta.project}` : ''}
                {siteMeta?.freq ? ` · ${siteMeta.freq}` : ''}
              </div>
              <div className="sh-dm-scroll">
                <table className="sh-dgrid">
                  <thead>
                    <tr>
                      <th className="dg-pt sh-th-sort"
                        onClick={() => setDrillDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                        title="Sort samples">
                        Sample <span className="sh-sort">{drillDir === 'asc' ? '▲' : '▼'}</span>
                      </th>
                      {months.map((m) => {
                        const [mon, yr] = m.label.split(' ');
                        return <th key={m.mk}>{mon}<br />{yr}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {points.map((p) => (
                      <tr key={p}>
                        <td className="dg-pt">{p}</td>
                        {months.map((m) => {
                          const st = ((g?.cells[p] ?? {})[m.mk] ?? 'na') as CellState;
                          return (
                            <td key={m.mk}>
                              <span className={`sh-cx ${st}`} title={`${p} — ${m.label}`}>{CELL_MARK[st]}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {points.length === 0 && (
                      <tr><td className="dg-pt" colSpan={months.length + 1}>No sample points listed for this site.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Prototype popup legend — per POINT the status is binary (a point's
                  sample was collected or not), so only ✓ / ✗ / · here (no Partial). */}
              <div className="sh-dm-legend">
                <span className="li"><span className="sh-cx ok">✓</span> Sampled</span>
                <span className="li"><span className="sh-cx miss">✗</span> Not sampled</span>
                <span className="li"><span className="sh-cx na">·</span> No sample that month</span>
              </div>
            </>
          );
        })()}
      </AppDialog>
    </div>
  );
}
